/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useCidModifierList.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    Custom hook for managing CID Modifiers list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchCidModifiers } from '../api/cidModifierApi';
import { CidModifier } from '../types';

interface UseCidModifierListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing CID Modifiers list
 * Implements Domain Contextual Filtering pattern
 */
export function useCidModifierList(options: UseCidModifierListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchCidModifiersData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchCidModifiers(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            domainId: effectiveDomainId,
          },
          params.search,
          params.sortBy,
        );

        return {
          data: result.data as CidModifier[],
          pagination: result.pagination,
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('cidModifiers.messages.loadError');
        showError(message);
        return {
          data: [] as CidModifier[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },
    [showError, t, effectiveDomainId],
  );

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('cidModifiers.fields.name.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'source',
        label: t('cidModifiers.fields.source.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'enabled',
        label: t('cidModifiers.fields.enabled.label'),
        visible: true,
        sortable: true,
      },
      {
        key: 'createdAt',
        label: t('common:createdAt'),
        visible: false,
        sortable: true,
      },
      {
        key: 'actions',
        label: t('common:actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t],
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData: fetchCidModifiersData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'cidModifiers',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as CidModifier[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

